import { GetStaticPaths, GetStaticProps } from 'next';
import Image from 'next/image';

import Layout from '@/components/Layout/Vanilla';
import { sdk } from '@/lib/graphcms';
import Breadcrump, { BreadcrumpPages } from '@/components/breadcrump';
import Container from '@/components/container';
import Heading from '@/components/Heading';
import Button from '@/components/Button';
import Content from '@/components/blog/content';
import { IntegrationContentPage } from '@/generated/graphcms-schema';
import { useScrollToAnchor } from '@/hooks/useScrollToAnchor';

type BlogPageProps = IntegrationContentPage & {
  preview: boolean;
  slug: string;
};

export default function IntegrationsContent({
  integration,
  preview,
  slug,
}: BlogPageProps) {
  useScrollToAnchor();

  const title = integration?.title || ``;

  const breadcrumpPages: BreadcrumpPages = [
    {
      name: `Integrations`,
      href: `/integrations`,
    },
    {
      name: title,
      href: `/integrations/${slug}`,
      current: true,
    },
  ];

  return (
    <Layout seo={{ title, meta: integration?.seo || {} }} preview={preview}>
      <Container>
        <Breadcrump pages={breadcrumpPages} />
      </Container>

      <div className="flex flex-col overflow-hidden pt-8 sm:pt-12 lg:relative lg:block lg:py-24">
        <div className="order-2 max-w-lg px-6 sm:mx-auto sm:max-w-3xl lg:order-1 lg:grid lg:max-w-6xl lg:grid-cols-2 lg:gap-24">
          <div className="space-y-6 lg:py-10">
            <Heading className="font-bold text-haiti">{title}</Heading>
            <p className="text-xl leading-relaxed text-zodiac md:text-[22px]">
              {integration?.description}
            </p>
            <div className="flex">
              <Button
                href="https://app.graphcms.com/signup"
                withArrow
                colour="purple"
              >
                Get started with GraphCMS
              </Button>
            </div>
          </div>
        </div>

        <div className="order-1 mb-6 sm:mx-auto sm:max-w-3xl sm:px-6 lg:order-2 lg:mb-0">
          <div className="flex sm:relative lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
            <div className="relative my-auto mx-0 flex rounded pl-4 pr-4 sm:max-w-3xl sm:px-0 lg:-mr-40 lg:max-w-none lg:pr-0 lg:pl-12">
              {integration?.image?.url && (
                <Image
                  width={750}
                  height={422}
                  src={integration.image.url}
                  alt={integration.image.alt || ``}
                  className="rounded ring-1 ring-black ring-opacity-5"
                  objectFit="cover"
                  quality={100}
                  priority
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Container className="relative my-10 md:mt-16 lg:mb-20">
        <Content content={integration?.content || ``} postTitle={title} />
      </Container>
    </Layout>
  );
}

export const getStaticProps: GetStaticProps = async ({
  preview = false,
  params,
}) => {
  const slug = params?.slug as string;

  const { integration } = await sdk({ preview }).integrationContentPage({
    slug,
  });

  if (!integration) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      preview,
      integration,
      slug,
    },
    revalidate: 3600,
  };
};

export const getStaticPaths: GetStaticPaths = async () =>
  Promise.resolve({
    paths: [],
    fallback: `blocking`,
  });
